import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    budget: '',
    timeline: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your inquiry! We\'ll be in touch within 24 hours.');
  };

  const contactMethods = [
    {
      icon: 'mail',
      title: 'Email Us',
      description: 'For general inquiries and project discussions',
      value: 'hello@nurrevo.com',
      action: 'mailto:hello@nurrevo.com'
    },
    {
      icon: 'phone',
      title: 'Call Us',
      description: 'Mon-Fri, 9am-6pm EST',
      value: '+1 (555) 123-4567',
      action: 'tel:+15551234567'
    },
    {
      icon: 'location_on',
      title: 'Visit Us',
      description: 'Schedule an in-person meeting',
      value: '123 Innovation Drive, Suite 400, San Francisco, CA 94105',
      action: null
    }
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity. A simple web application might take 2-3 months, while enterprise solutions can span 6-18 months. We\'ll provide a detailed timeline during our initial consultation.'
    },
    {
      question: 'What is your pricing model?',
      answer: 'We offer both fixed-price and time & materials models depending on project scope. Fixed-price works well for well-defined projects, while T&M offers flexibility for evolving requirements. Most projects range from $50K to $500K+.'
    },
    {
      question: 'Do you work with startups?',
      answer: 'We work with companies of all sizes. For startups, we often recommend starting with an MVP and iterating based on user feedback. We can also advise on technical strategy and help you build scalable foundations.'
    },
    {
      question: 'What happens after the project launches?',
      answer: 'We offer comprehensive support and maintenance packages. Most clients continue working with us for ongoing enhancements, scaling, and support. We\'re invested in your long-term success.'
    }
  ];

  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 lg:py-28">
        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium text-gray-300 mb-6">
              <span className="material-symbols-outlined text-lg">chat</span>
              Let's Talk
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Start Building 
              <span className="text-white/90"> Something Great</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Ready to transform your ideas into reality? Tell us about your project and let's explore how we can help you succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <div key={index} className="text-center p-6 rounded-2xl hover:bg-gray-50 transition-colors">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center text-gray-700 mx-auto mb-4">
                  <span className="material-symbols-outlined text-2xl">{method.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{method.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{method.description}</p>
                {method.action ? (
                  <a 
                    href={method.action}
                    className="text-gray-900 font-semibold hover:underline"
                  >
                    {method.value}
                  </a>
                ) : (
                  <p className="text-gray-700 text-sm">{method.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell Us About Your Project</h2>
              <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="Acme Inc."
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">
                    What can we help you with? *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="custom-development">Custom Software Development</option>
                    <option value="cloud-devops">Cloud & DevOps</option>
                    <option value="ai-ml">AI & Machine Learning</option>
                    <option value="mobile-apps">Mobile App Development</option>
                    <option value="consulting">Technical Consulting</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-semibold text-gray-900 mb-2">
                      Budget Range
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select budget range</option>
                      <option value="under-50k">Under $50,000</option>
                      <option value="50k-100k">$50,000 - $100,000</option>
                      <option value="100k-250k">$100,000 - $250,000</option>
                      <option value="250k-500k">$250,000 - $500,000</option>
                      <option value="over-500k">Over $500,000</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                      Expected Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select timeline</option>
                      <option value="asap">ASAP</option>
                      <option value="1-3-months">1-3 months</option>
                      <option value="3-6-months">3-6 months</option>
                      <option value="6-12-months">6-12 months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your project, goals, and any specific requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gray-900 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                >
                  Send Message
                  <span className="material-symbols-outlined text-xl ml-2">send</span>
                </button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting, you agree to our <Link to="/privacy" className="text-gray-900 hover:underline">Privacy Policy</Link>
                </p>
              </form>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* Why Work With Us */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Why Companies Choose Nurrevo</h3>
                <div className="space-y-4">
                  {[
                    { icon: 'verified', text: 'Senior-only team with 8+ years average experience' },
                    { icon: 'schedule', text: '98% of projects delivered on time and on budget' },
                    { icon: 'support_agent', text: 'Dedicated project manager and 24/7 support' },
                    { icon: 'security', text: 'Enterprise-grade security and compliance' },
                    { icon: 'handshake', text: 'Transparent communication and weekly updates' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-gray-700 text-xl mt-0.5">{item.icon}</span>
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b border-gray-100 pb-4 last:border-0">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="flex items-center justify-between w-full text-left"
                      >
                        <span className="font-semibold text-gray-900">{faq.question}</span>
                        <span className="material-symbols-outlined text-gray-400 text-xl">
                          {expandedFaq === index ? 'expand_less' : 'expand_more'}
                        </span>
                      </button>
                      {expandedFaq === index && (
                        <p className="text-gray-600 text-sm mt-3 animate-fadeIn">{faq.answer}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gray-900 p-8 rounded-2xl text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-2xl">bolt</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Quick Response</h3>
                    <p className="text-gray-300">We respond within 24 hours</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">
                  Every inquiry is personally reviewed by a senior consultant. You'll receive a thoughtful response, not an automated email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="h-96 bg-gray-200 relative">
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <span className="material-symbols-outlined text-6xl text-gray-400">map</span>
            <p className="text-gray-500 mt-4">Interactive Map</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
